import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Eyebrow } from "@/components/Section";
import { useI18n } from "@/context/I18nContext";
import { SEO, serviceJsonLd, breadcrumbJsonLd } from "@/components/SEO";

export const ServicePage = ({
  eyebrow,
  title,
  subtitle,
  heroImage,
  features = [],
  features_intro,
  bullets = [],
  longText = [],
  steps = [],
  testimonials = [],
  cta_label,
  testIdSlug,
  seo,
  slotAfterHero,
  slotAfterFeatures,
}) => {
  const { t } = useI18n();
  const jsonLd = seo
    ? [serviceJsonLd(seo.serviceName || title, seo.description || subtitle || ""), breadcrumbJsonLd(seo.breadcrumb || [])]
    : null;

  return (
    <div data-testid={`service-page-${testIdSlug}`} className="relative">
      {seo && (
        <SEO
          title={seo.title}
          description={seo.description}
          keywords={seo.keywords}
          jsonLd={jsonLd}
          type="service"
        />
      )}
      {/* HERO */}
      <section className="relative overflow-hidden bg-soft">
        <div className="absolute inset-0 bg-hero-glow pointer-events-none" />
        <div className="absolute inset-0 dot-grid opacity-40 pointer-events-none" />
        {heroImage && (
          <div className="absolute inset-0 opacity-10 pointer-events-none">
            <img src={heroImage} alt="" className="w-full h-full object-cover" aria-hidden="true" />
          </div>
        )}
        <div className="max-w-7xl mx-auto px-6 sm:px-12 py-20 sm:py-32 relative grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-9">
            <Eyebrow>{eyebrow}</Eyebrow>
            <h1 className="mt-6 font-display font-black text-4xl sm:text-6xl lg:text-7xl tracking-tightest leading-[0.95] text-ink">
              {title}
            </h1>
            {subtitle && <p className="mt-8 max-w-2xl text-lg text-muted leading-relaxed">{subtitle}</p>}
            <div className="mt-8">
              <Link
                to="/kontakt"
                data-testid={`${testIdSlug}-cta`}
                className="inline-flex items-center gap-2 px-7 py-3.5 bg-primary text-white font-medium hover:bg-primary-hover transition-colors shadow-soft"
              >
                {cta_label || t.cta.get_started}
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {slotAfterHero}

      {/* FEATURES */}
      {features.length > 0 && (
        <section className="bg-white border-y border-line">
          <div className="max-w-7xl mx-auto px-6 sm:px-12 py-24 sm:py-28">
            {features_intro && (
              <div className="max-w-2xl mb-14">
                <Eyebrow>Leistungen</Eyebrow>
                <p className="mt-4 text-muted text-lg leading-relaxed">{features_intro}</p>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map((f, idx) => (
                <div
                  key={f.title}
                  className="border border-line bg-white p-8 lift"
                  data-testid={`feature-${testIdSlug}-${idx}`}
                >
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-accent">
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                  <h3 className="mt-5 font-display font-bold text-xl text-ink">{f.title}</h3>
                  <p className="mt-3 text-muted text-sm leading-relaxed">{f.desc}</p>
                  {f.points && (
                    <ul className="mt-5 space-y-2">
                      {f.points.map((p) => (
                        <li key={p} className="flex items-start gap-2 text-sm text-ink/90">
                          <CheckCircle2 size={15} className="text-accent mt-0.5 flex-shrink-0" /> {p}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {slotAfterFeatures}

      {/* BULLETS */}
      {bullets.length > 0 && (
        <section className="bg-soft border-b border-line">
          <div className="max-w-7xl mx-auto px-6 sm:px-12 py-24 sm:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <Eyebrow>Highlights</Eyebrow>
              <h2 className="mt-4 font-display font-black text-3xl sm:text-4xl tracking-tightest text-ink">
                Was Sie bekommen.
              </h2>
            </div>
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 border-t border-line">
              {bullets.map((b, i) => (
                <div key={i} className="py-5 border-b border-line flex items-center gap-3 text-ink">
                  <span className="font-mono text-xs text-primary">{String(i + 1).padStart(2, "0")}</span>
                  {b}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* STEPS */}
      {steps.length > 0 && (
        <section className="bg-white border-b border-line">
          <div className="max-w-7xl mx-auto px-6 sm:px-12 py-24 sm:py-28">
            <div className="max-w-2xl">
              <Eyebrow>Vorgehen</Eyebrow>
              <h2 className="mt-4 font-display font-black text-3xl sm:text-4xl tracking-tightest text-ink">
                So gehen wir vor.
              </h2>
            </div>
            <ol className="mt-14 space-y-2">
              {steps.map((s, i) => (
                <li key={i} className="grid grid-cols-12 gap-6 border-b border-line py-7 items-baseline">
                  <span className="col-span-2 sm:col-span-1 font-display font-black text-3xl text-accent">{String(i + 1).padStart(2, "0")}</span>
                  <div className="col-span-10 sm:col-span-11">
                    <h4 className="font-display font-bold text-xl text-ink">{s.title}</h4>
                    <p className="mt-2 text-muted text-sm leading-relaxed">{s.body}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>
      )}

      {/* LONG TEXT */}
      {longText.length > 0 && (
        <section className="bg-soft border-b border-line">
          <div className="max-w-4xl mx-auto px-6 sm:px-12 py-24 sm:py-28">
            <Eyebrow>Hintergrund</Eyebrow>
            <div className="mt-6 space-y-6 text-muted text-lg leading-relaxed">
              {longText.map((p, i) => (
                <p key={i} dangerouslySetInnerHTML={{ __html: p }} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* TESTIMONIALS */}
      {testimonials.length > 0 && (
        <section className="bg-white border-b border-line">
          <div className="max-w-7xl mx-auto px-6 sm:px-12 py-24 sm:py-28">
            <Eyebrow>Stimmen</Eyebrow>
            <h2 className="mt-4 font-display font-black text-3xl sm:text-4xl tracking-tightest mb-12 text-ink">
              Was unsere Kunden sagen.
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {testimonials.map((t, i) => (
                <figure key={i} className="border border-line bg-white p-8 shadow-soft">
                  <blockquote className="text-ink leading-relaxed">„{t.quote}"</blockquote>
                  <figcaption className="mt-6 pt-6 border-t border-line">
                    <div className="font-display font-bold text-ink">{t.name}</div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted mt-1">{t.role}</div>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FOOTER CTA */}
      <section className="bg-primary text-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 py-20 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <span className="font-mono text-xs uppercase tracking-[0.25em] text-accent">[ Nächster Schritt ]</span>
            <h2 className="mt-4 font-display font-black text-3xl sm:text-4xl tracking-tightest">
              Lassen Sie uns konkret werden.
            </h2>
          </div>
          <div className="lg:col-span-4 flex gap-3">
            <Link to="/kontakt" className="flex-1 inline-flex items-center justify-center gap-2 px-7 py-4 bg-accent text-ink font-medium hover:bg-accent/90 transition-colors">
              {t.cta.contact_us} <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
